/**
 * Map Layout Presets System
 * Système de presets intelligents pour adapter l'interface selon le contexte
 */

export const MAP_PRESET_IDS = [
  'planning',
  'exploration',
  'booking',
  'analytics',
  'discovery',
  'navigation',
  'drive',
  'explore',
  'report',
  'ar',
  'scout',
  'quickPlan',
  'studioPro',
  'bookingHub',
  'discoveryCanvas',
  'analyticsDashboard'
] as const

export type MapPresetId = typeof MAP_PRESET_IDS[number]

export type DeviceType = 'web' | 'mobile' | 'tablet' | 'both'

export type LayoutType =
  | 'split'
  | 'fullscreen'
  | 'triple'
  | 'tri-panel'
  | 'split-compare'
  | 'dashboard'
  | 'overlay'
  | 'bottom-sheet'

export type PanelPosition = 'left' | 'right' | 'top' | 'bottom' | 'overlay' | 'floating'

export type AIFeatureType = 'suggestion' | 'optimization' | 'alert' | 'insight' | 'recommendation'

export type TriggerOperator = 'AND' | 'OR'

export type ConditionOperator = '==' | '!=' | '>' | '<' | '>=' | '<=' | 'in' | 'not_in'

export interface MapPreset {
  id: MapPresetId
  name: string
  description: string
  icon: string
  device: DeviceType
  layout: LayoutConfig
  panels: PanelConfig[]
  aiFeatures: AIFeature[]
  autoTrigger?: TriggerRule[]
  shortcuts?: KeyboardShortcut[]
  analytics?: AnalyticsConfig
}

export interface LayoutConfig {
  type: LayoutType
  mapSize: number // 0-100%
  orientation: 'horizontal' | 'vertical'
  panelsSizes?: Record<string, number>
  animations?: AnimationConfig
  responsiveBreakpoints?: ResponsiveBreakpoint[]
}

export interface PanelConfig {
  id: string
  name: string
  component: string
  position: PanelPosition
  size: number | 'auto'
  minSize?: number
  maxSize?: number
  collapsible: boolean
  resizable: boolean
  swipeable: boolean
  defaultCollapsed?: boolean
  zIndex?: number
  transition?: MapTransitionConfig
  tabs?: Array<{
    id: string
    label: string
    icon?: string
    component: string
  }>
  metadata?: Record<string, unknown>
  widgets?: Array<Record<string, unknown>>
}

export interface AIFeature {
  id: string
  name: string
  type: AIFeatureType
  trigger: TriggerRule
  priority: 'low' | 'medium' | 'high' | 'critical'
  cooldown?: number // ms entre suggestions
  dismissible: boolean
  action?: AIAction
  template?: string // Template du message
}

export interface TriggerRule {
  conditions: Condition[]
  operator: TriggerOperator
  debounce?: number // ms
}

export interface Condition {
  key: string // e.g., 'speed', 'time', 'location'
  operator: ConditionOperator
  value: any
  tolerance?: number // Pour valeurs numériques
}

export interface AIAction {
  type: 'navigate' | 'suggest' | 'alert' | 'optimize' | 'book'
  payload?: Record<string, any>
  callback?: () => void
}

export interface KeyboardShortcut {
  key: string
  modifiers?: ('ctrl' | 'shift' | 'alt' | 'meta')[]
  action: string
  description: string
}

export interface AnimationConfig {
  enabled: boolean
  duration: number // ms
  easing: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear'
}

export interface MapTransitionConfig {
  enter: string // CSS class
  leave: string // CSS class
  duration: number // ms
}

export interface ResponsiveBreakpoint {
  minWidth: number
  maxWidth: number
  layout: Partial<LayoutConfig>
}

export interface AnalyticsConfig {
  trackInteractions: boolean
  trackAI: boolean
  trackSwitches: boolean
  customEvents?: string[]
}

/**
 * AI Context - Données contextuelles pour triggers
 */
export interface AIContext {
  // Location
  location?: {
    lat: number
    lng: number
  }
  speed?: number // km/h
  heading?: number // degrees
  altitude?: number // meters

  // Time
  time?: Date
  timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night'
  dayOfWeek?: number // 0-6

  // User
  drivingTime?: number // minutes depuis départ
  breakTime?: number // minutes depuis dernière pause
  fatigueLevel?: number // 0-100
  travelMode?: 'driving' | 'bicycle' | 'walking'

  // Trip
  activePlanId?: string
  stageCount?: number
  completedStages?: number
  remainingDistance?: number // km
  eta?: Date

  // Budget
  budget?: {
    total: number
    spent: number
    remaining: number
  }

  // Weather
  weather?: {
    condition: string
    temperature: number
    rain: boolean
    wind: number
  }

  // Device
  device: DeviceType
  batteryLevel?: number // 0-100
  networkStatus?: 'online' | 'offline' | 'slow'

  // UI State
  currentPreset?: MapPresetId
  searchActive?: boolean
  editingStages?: boolean
  viewingStats?: boolean
  bookingMode?: boolean

  // User Preferences
  preferences?: {
    language: string
    currency: string
    units: 'metric' | 'imperial'
    aiSuggestionsEnabled: boolean
    mapPresetId?: MapPresetId
  }
}

/**
 * Preset Metrics - Pour analytics
 */
export interface PresetMetrics {
  presetId: MapPresetId
  startTime: Date
  endTime?: Date
  duration: number // ms
  interactions: number
  panelSwitches: number
  aiSuggestionsShown: number
  aiSuggestionsAccepted: number
  aiSuggestionsDismissed: number
  switchReason: 'manual' | 'auto' | 'ai' | 'shortcut'
  fromPreset?: MapPresetId
  userSatisfaction?: 1 | 2 | 3 | 4 | 5
  errorCount?: number
  customMetrics?: Record<string, any>
}

/**
 * Transition Options - Pour animations entre presets
 */
export interface TransitionOptions {
  animated?: boolean
  duration?: number // ms
  easing?: string
  onComplete?: () => void
  direction?: 'left' | 'right' | 'up' | 'down' | 'fade'
}

/**
 * Preset Switch Request - Demande de changement de preset
 */
export interface PresetSwitchRequest {
  toPreset: MapPresetId
  reason: 'manual' | 'auto' | 'ai' | 'shortcut'
  context?: Partial<AIContext>
  options?: TransitionOptions
  silent?: boolean // Ne pas notifier l'utilisateur
}

/**
 * Map AI Suggestion - Suggestion générée par l'IA pour les presets MAP
 */
export interface MapAISuggestion {
  id: string
  featureId: string
  type: AIFeatureType
  priority: 'low' | 'medium' | 'high' | 'critical'
  title: string
  message: string
  icon?: string
  action?: AIAction
  dismissible: boolean
  expiresAt?: Date
  metadata?: Record<string, any>
}

/**
 * Panel State - État d'un panel
 */
export interface PanelState {
  id: string
  visible: boolean
  collapsed: boolean
  size: number | 'auto'
  position: PanelPosition
  zIndex: number
  data?: any
}

/**
 * Map Preset State - État global du système de presets
 */
export interface MapPresetState {
  currentPreset: MapPresetId
  previousPreset?: MapPresetId
  context: AIContext
  panels: Record<string, PanelState>
  aiSuggestions: MapAISuggestion[]
  metrics: PresetMetrics
  isTransitioning: boolean
  userOverrides?: Partial<LayoutConfig>
  lastSwitchReason?: PresetSwitchRequest['reason']
}
